import { Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { RequiredDocumentService, RequiredDocumentFilterURL } from '@shared/services/api/required-document.service';
import { RequiredDocumentFiltersService } from '@shared/services/api/required-document-filters.service';
import { Observable, of, switchMap, tap } from 'rxjs';
import { RequiredDocument } from '@shared/entities/requiredDocument';
import { RequiredDocumentFilter } from '@shared/entities/requiredDocumentFilter';
import { FilterSelectedEventType, FilterOrSortDropdownType } from '@shared/models/types';
import { Router } from '@angular/router';

@Component({
  selector: 'app-required-document',
  templateUrl: './required-document.component.html',
  styleUrl: './required-document.component.scss'
})
export class RequiredDocumentComponent implements OnInit, OnDestroy {
  showSuccess = false;
  showError = false;

  allRequiredDocuments$: Observable<RequiredDocument[]>;
  requiredDocumentFilter$: Observable<RequiredDocumentFilter>;

  reqDocPrevPage?: number = undefined;
  reqDocNextPage?: number = undefined;
  reqDocCurrentPage = 1;
  reqDocTotalPages?: number = undefined;

  requiredDocumentFilters: any = {};

  constructor(
    private _requiredDocumentService: RequiredDocumentService,
    private _requiredDocumentFiltersService: RequiredDocumentFiltersService,
    private _location: Location,
    private _router: Router
  ) {
    this.allRequiredDocuments$ = this._requiredDocumentService.allRequiredDocuments$.pipe(
      switchMap((paginatedResult) => {
        this.reqDocPrevPage = paginatedResult.prevPage;
        this.reqDocCurrentPage = paginatedResult.currentPage ?? 1;
        this.reqDocNextPage = paginatedResult.nextPage;
        this.reqDocTotalPages = paginatedResult.totalPage;
        return of(paginatedResult.data);
      })
    );

    this.requiredDocumentFilter$ = this._requiredDocumentFiltersService.all$;
  }

  goBack() {
    this._location.back();
  }

  goToRepository() {
    this._router.navigate(['/repository']);
  }

  ngOnInit(): void {
    this._requiredDocumentService.refresh(1).subscribe();
    this._requiredDocumentFiltersService.refresh(true).subscribe();
  }

  onPageSelected(page: number) {
    this._requiredDocumentService
      .refresh(page)
      .pipe(
        tap((paginatedResult) => {
          this.reqDocPrevPage = paginatedResult.prevPage;
          this.reqDocCurrentPage = paginatedResult.currentPage ?? 1;
          this.reqDocNextPage = paginatedResult.nextPage;
          this.reqDocTotalPages = paginatedResult.totalPage;
        })
      )
      .subscribe();
  }

  onFilterSelected(filters: FilterSelectedEventType[]) {
    const filtersName = filters.reduce((acc: any, filter: FilterSelectedEventType) => {
      acc[filter.name] = filter.selectedValue;
      return acc;
    }, {});

    this.requiredDocumentFilters = { ...filtersName };
    this._requiredDocumentService.filters = filtersName as RequiredDocumentFilterURL;
    this._requiredDocumentFiltersService.filters = filtersName as RequiredDocumentFilterURL;
  }

  getFilterFields(filterData: any): FilterOrSortDropdownType[] {
    const fields: FilterOrSortDropdownType[] = [];

    if (!filterData) {
      return fields;
    }

    for (const key in filterData) {
      if (Object.prototype.hasOwnProperty.call(filterData, key) && key !== 'orderBy') {
        const values = filterData[key];
        if (values && Array.isArray(values)) {
          fields.push({
            key: key,
            values: values.map((value: string) => ({
              id: value,
              value: value
            }))
          });
        }
      }
    }

    return fields;
  }

  getSortingOptions(filterData: any): FilterOrSortDropdownType[] {
    const options: FilterOrSortDropdownType[] = [];

    if (!filterData || !filterData.orderBy) {
      return options;
    }

    options.push({
      key: 'orderBy',
      values: filterData.orderBy.map((value: string) => ({
        id: value,
        value: value
      }))
    });

    return options;
  }

  resetFilters() {
    this.requiredDocumentFilters = {};
    this._requiredDocumentService.filters = {} as RequiredDocumentFilterURL;
    this._requiredDocumentFiltersService.filters = {} as RequiredDocumentFilterURL;
  }

  getYear() {
    return new Date().getFullYear();
  }

  ngOnDestroy(): void {
    // Reset filters when leaving the page
    this.resetFilters();
  }
}
